<template>
    <div>
        <card-header title="Wall Post" icon="fa-images"/>

        <card-body>
            <card-list>
                <subheader-list-item icon="fa-align-left" title="Post"/>
                <container-list-item>
                    <data-row title="Author">{{ post.author_name }} <b-tag size="is-small" class="ml-2" v-if="post.is_staff_post">Staff</b-tag></data-row>
                    <data-row title="Posted">{{ post.created_at | moment('MMM Do YYYY, h:mma') }}</data-row>
                    <data-row title="In Navigator">{{ post.hidden ? 'Hidden from the group' : 'Visible to everyone' }}</data-row>
                </container-list-item>
                <container-list-item padded no-border v-if="post.body || (post.photos && post.photos.length)">
                    <p class="post-body mb-3" v-if="post.body">{{ post.body }}</p>
                    <div class="photo-strip" v-if="post.photos && post.photos.length">
                        <a v-for="photo in post.photos" :key="photo.id" :href="photoUrl(photo, 'feed')" target="_blank">
                            <!-- Videos serve the original file whatever size is
                                 asked for; the browser tab plays it. -->
                            <span class="video-cell" v-if="isVideo(photo)">
                                <video :src="photoUrl(photo, 'feed') + '#t=0.001'" preload="metadata" muted></video>
                                <span class="play-badge"><i class="fas fa-play"></i></span>
                            </span>
                            <img v-else :src="photoUrl(photo, 'thumb')"/>
                        </a>
                    </div>
                </container-list-item>
            </card-list>

            <card-list>
                <subheader-list-item icon="fa-comments" title="Comments" :badge="comments.length || null"/>
                <container-list-item padded v-if="!comments.length">
                    <p class="has-text-grey is-italic mb-0">No comments yet.</p>
                </container-list-item>
                <container-list-item padded v-for="comment in comments" :key="comment.id">
                    <div class="columns is-gapless is-mobile mb-1">
                        <div class="column">
                            <strong>{{ comment.author_name }}</strong>
                            <b-tag type="is-primary" size="is-small" class="ml-2" v-if="comment.is_staff_post">Staff</b-tag>
                            <b-tag size="is-small" class="ml-2" v-if="comment.hidden"><i class="fas fa-eye-slash mr-1"></i>Hidden</b-tag>
                        </div>
                        <div class="column is-narrow">
                            <span class="has-text-grey is-size-7 mr-3">{{ comment.created_at | moment('MMM Do, h:mma') }}</span>
                            <a class="mr-3" @click="hideComment(comment)">{{ comment.hidden ? 'Unhide' : 'Hide' }}</a>
                            <a class="has-text-danger" @click="deleteComment(comment)">Delete</a>
                        </div>
                    </div>
                    <p class="mb-0 comment-body">{{ comment.body }}</p>
                </container-list-item>
                <container-list-item padded no-border>
                    <b-field class="mb-0">
                        <b-input v-model="newReply" size="is-small" placeholder="Comment as staff..." expanded @keyup.enter.native="sendReply"/>
                        <p class="control">
                            <b-button size="is-small" type="is-primary" :disabled="!newReply.trim()" @click="sendReply">Send</b-button>
                        </p>
                    </b-field>
                </container-list-item>
            </card-list>
        </card-body>

        <card-footer class="pt-3 pb-3">
            <div class="columns">
                <div class="column">
                    <b-button type="is-warning is-outlined" size="is-small" class="mr-2" @click="toggleHide">
                        <i class="far mr-1" :class="post.hidden ? 'fa-eye' : 'fa-eye-slash'"></i>{{ post.hidden ? 'Unhide' : 'Hide from group' }}
                    </b-button>
                    <b-button type="is-danger is-outlined" size="is-small" @click="deletePost">
                        <i class="far fa-trash-alt"></i><span class="ml-2" v-if="confirmDelete">Delete?</span>
                    </b-button>
                </div>
            </div>
        </card-footer>
    </div>
</template>

<script>
    import CardHeader from "@/TIER/components/CardHeader";
    import CardBody from "@/TIER/components/CardBody";
    import CardFooter from "@/TIER/components/CardFooter";
    import CardList from "@/TIER/components/CardList";
    import SubheaderListItem from "@/TIER/components/SubheaderListItem";
    import ContainerListItem from "@/TIER/components/ContainerListItem";
    import DataRow from "@/TIER/components/DataRow";

    import {client as http} from '@/http_client';
    import {mapGetters} from "vuex";

    export default {
        name: "WallPost",
        props: ['card', 'props'],
        components: {DataRow, ContainerListItem, SubheaderListItem, CardList, CardFooter, CardBody, CardHeader},
        data: function() {
            return {
                post: {photos: []},
                comments: [],
                newReply: '',
                confirmDelete: false
            };
        },
        computed: {
            ...mapGetters(['currentUser'])
        },
        methods: {
            load: function() {
                this.$emit('loading', true);
                http.get('/api/experience/posts/' + this.props.postId, {force: true}).then(response => {
                    this.post = response.data;
                    this.loadComments();
                });
            },
            reload: function() {
                this.load();
            },
            loadComments: function() {
                http.get('/api/experience/posts/' + this.props.postId + '/comments', {force: true}).then(response => {
                    this.comments = response.data.comments;
                    this.$emit('loading', false);
                });
            },
            photoUrl: function(photo, size) {
                return '/api/experience/photo/' + photo.file + '/' + size + '?auth=' + this.currentUser.token;
            },
            isVideo: function(photo) {
                return !!photo.file_mime && photo.file_mime.indexOf('video/') === 0;
            },
            toggleHide: function() {
                http.put('/api/experience/posts/' + this.props.postId + '/hide', {hidden: this.post.hidden ? 0 : 1}, {force: true}).then(() => {
                    this.post.hidden = this.post.hidden ? 0 : 1;
                    this.$reloadCard('trip-wall', {tripId: this.props.tripId});
                });
            },
            deletePost: function() {
                if (this.confirmDelete) {
                    http.delete('/api/experience/posts/' + this.props.postId, {force: true}).then(() => {
                        this.$reloadCard('trip-wall', {tripId: this.props.tripId});
                        this.$closeCard(this.card);
                    });
                } else {
                    this.confirmDelete = true;
                    setTimeout(() => {
                        this.confirmDelete = false;
                    }, 3000);
                }
            },
            sendReply: function() {
                if (!this.newReply.trim()) return;
                http.post('/api/experience/posts/' + this.props.postId + '/comments', {body: this.newReply}, {force: true}).then(() => {
                    this.newReply = '';
                    this.loadComments();
                    this.$reloadCard('trip-wall', {tripId: this.props.tripId});
                });
            },
            hideComment: function(comment) {
                http.put('/api/experience/comments/' + comment.id + '/hide', {hidden: comment.hidden ? 0 : 1}, {force: true}).then(() => {
                    this.loadComments();
                });
            },
            deleteComment: function(comment) {
                http.delete('/api/experience/comments/' + comment.id, {force: true}).then(() => {
                    this.loadComments();
                    this.$reloadCard('trip-wall', {tripId: this.props.tripId});
                });
            }
        },
        watch: {
            'props.postId': function() {
                this.load();
            }
        },
        mounted() {
            this.load();
        }
    };
</script>

<style lang="scss" scoped>
    .post-body,
    .comment-body {
        white-space: pre-wrap;
        word-break: break-word;
    }

    .photo-strip {
        display: flex;
        flex-wrap: wrap;
        gap: 0.3rem;

        img,
        video {
            width: 110px;
            height: 110px;
            object-fit: cover;
            border-radius: 4px;
        }

        .video-cell {
            position: relative;
            display: inline-block;

            video {
                display: block;
                background-color: black;
            }

            .play-badge {
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                width: 2rem;
                height: 2rem;
                border-radius: 50%;
                background-color: rgba(0, 0, 0, 0.55);
                color: white;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 0.7rem;
                pointer-events: none;
            }
        }
    }
</style>
